import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { MoreHorizontal, UserCheck, UserX, Info } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import EditableCell from './EditableCell';
import EditableStatusCell from './EditableStatusCell';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface Purchase {
  date: any;
  amount: number;
  actionsAdded: number;
  paymentId: string;
  method?: string;
}

interface User {
  uid: string;
  email: string;
  displayName: string;
  photoURL?: string;
  actionsRemaining: number;
  isPremium: boolean;
  isAdmin?: boolean;
  purchases?: Purchase[] | number;
  createdAt: any;
  lastLogin: any;
  gameState?: {
    elements?: any[];
    favorites?: any[];
    discoveries?: any[];
    goals?: any[];
    points?: number;
    skipCount?: number;
    keyCount?: number;
    zoomLevel?: number;
    wordHistory?: {
      Item: string[];
      Location: string[];
      Character: string[];
    };
    stats?: {
      totalDiscoveries?: number;
      goalsCompleted?: number;
      [key: string]: any;
    };
  };
}

interface UserTableProps {
  users: User[];
  onViewUser: (userId: string) => void;
  onEditUser: (userId: string) => void;
  onMakeAdmin: (userId: string) => void;
  onRemoveAdmin: (userId: string) => void;
  onUpdateField: (userId: string, field: string, value: any) => Promise<boolean>;
  onUpdatePremiumStatus: (userId: string, isPremium: boolean) => Promise<boolean>;
}

const UserTable: React.FC<UserTableProps> = ({
  users,
  onViewUser,
  onEditUser,
  onMakeAdmin,
  onRemoveAdmin,
  onUpdateField,
  onUpdatePremiumStatus,
}) => {
  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';

    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>User</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Purchases</TableHead>
            <TableHead>Actions</TableHead>
            <TableHead>Keys</TableHead>
            <TableHead>Skips</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {users.map((user) => (
            <TableRow key={user.uid}>
              {/* User Name */}
              <TableCell className="font-medium">
                <div className="flex items-center gap-2">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center overflow-hidden cursor-pointer">
                          {user.photoURL ? (
                            <img
                              src={user.photoURL}
                              alt={user.displayName || 'User'}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <span>
                              {user.displayName?.charAt(0) || user.email?.charAt(0) || 'U'}
                            </span>
                          )}
                        </div>
                      </TooltipTrigger>
                      <TooltipContent className="w-80 p-0">
                        <div className="bg-card rounded-md shadow-md p-3">
                          <h4 className="font-medium mb-2">User Details</h4>
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <div>
                              <p className="text-muted-foreground">Joined:</p>
                              <p>{formatDate(user.createdAt)}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Last Login:</p>
                              <p>{formatDate(user.lastLogin)}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Current Goals:</p>
                              <p>{user.gameState?.goals?.length || 0}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Goals Completed:</p>
                              <p>{user.gameState?.stats?.goalsCompleted || 0}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Elements:</p>
                              <p>{user.gameState?.elements?.length || 0}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Points:</p>
                              <p>{user.gameState?.points || 0}</p>
                            </div>
                          </div>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <span>{user.displayName || 'Unnamed User'}</span>
                </div>
              </TableCell>

              {/* Email */}
              <TableCell className="text-sm text-muted-foreground">{user.email}</TableCell>

              {/* Purchases */}
              <TableCell>
                {Array.isArray(user.purchases)
                  ? user.purchases.length
                  : typeof user.purchases === 'number'
                  ? user.purchases
                  : 0}
              </TableCell>

              {/* Actions Remaining - Editable */}
              <EditableCell
                value={user.actionsRemaining}
                type="number"
                onSave={(newValue) => onUpdateField(user.uid, 'actionsRemaining', newValue)}
              />

              {/* Keys - Editable */}
              <EditableCell
                value={user.gameState?.keyCount || 0}
                type="number"
                onSave={(newValue) => onUpdateField(user.uid, 'gameState.keyCount', newValue)}
              />

              {/* Skips - Editable */}
              <EditableCell
                value={user.gameState?.skipCount || 0}
                type="number"
                onSave={(newValue) => onUpdateField(user.uid, 'gameState.skipCount', newValue)}
              />

              {/* Status - Editable */}
              <EditableStatusCell
                isPremium={user.isPremium || false}
                isAdmin={user.isAdmin || false}
                onUpdatePremium={(isPremium) => onUpdatePremiumStatus(user.uid, isPremium)}
                onUpdateAdmin={(isAdmin) =>
                  isAdmin ? onMakeAdmin(user.uid) : onRemoveAdmin(user.uid)
                }
              />

              {/* Actions Menu */}
              <TableCell className="text-right">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon">
                      <MoreHorizontal className="h-4 w-4" />
                      <span className="sr-only">Open menu</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuLabel>Actions</DropdownMenuLabel>

                    <DropdownMenuItem onClick={() => onViewUser(user.uid)}>
                      <Info className="h-4 w-4 mr-2" />
                      View Details
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default UserTable;
